import React, { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Bot, Send, Loader2, Sparkles, User as UserIcon, Zap, Plus, MessageSquare, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { Switch } from '@/components/ui/switch';
import { DraftActionCard, AgentMarkdown } from '@/components/agent/DraftActionCard';

const QUICK_PROMPTS = [
  'Show my patients on trial',
  "What's my follow-up list today?",
  'Follow up Shyam Sundar on WhatsApp about his trial',
  'Find the Signia Styletto IX video and send it to my latest trial patient',
  'Make a PDF answering: is it visible, battery life, and price vs value',
  'Call my newest hot lead and book a free hearing test',
];

const makeSessionId = (uid) => `agent-${uid || 'anon'}-${Date.now()}`;

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
    if (diffMin < 10080) return `${Math.floor(diffMin / 1440)}d ago`;
    return d.toLocaleDateString();
  } catch { return ''; }
}

export default function AgentChatPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(() => makeSessionId(user?.id));
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [autoSend, setAutoSend] = useState(false);
  const bottomRef = useRef(null);

  const toggleAutoSend = async (v) => {
    setAutoSend(v);
    try { await api.put('/agent-chat/settings', { agent_auto_send: v }); toast.success(v ? 'Agent will send & call straight away' : 'Agent will draft first — you tap Send/Call'); }
    catch { setAutoSend(!v); toast.error('Could not save setting'); }
  };
  const updateAction = (mi, upd) => setMessages((ms) => ms.map((m, i) => (i !== mi ? m : { ...m, actions: m.actions.map((a) => (a.action_id === upd.action_id ? upd : a)) })));

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const r = await api.get('/agent-chat/sessions', { params: { limit: 40 } });
      setSessions(r.data?.sessions || []);
    } catch (e) { /* non-fatal */ }
    finally { setSessionsLoading(false); }
  }, []);

  useEffect(() => {
    api.get('/agent-chat/scope').then(r => setScope(r.data)).catch(() => {});
    api.get('/agent-chat/settings').then(r => setAutoSend(!!r.data.agent_auto_send)).catch(() => {});
    loadSessions();
  }, [loadSessions]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const openSession = async (sid) => {
    setSessionId(sid);
    setMessages([]);
    try {
      const r = await api.get(`/agent-chat/sessions/${encodeURIComponent(sid)}/messages`);
      setMessages((r.data?.messages || []).map(m => ({
        role: m.role, content: m.content, tool_used: m.tool_used, latency_ms: m.latency_ms, actions: m.actions || [],
      })));
    } catch (e) {
      toast.error('Could not load that chat');
    }
  };

  const newChat = () => {
    setSessionId(makeSessionId(user?.id));
    setMessages([]);
    setInput('');
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this chat permanently?')) return;
    try {
      await api.delete(`/agent-chat/sessions/${encodeURIComponent(sid)}`);
      setSessions(s => s.filter(x => x.session_id !== sid));
      if (sid === sessionId) newChat();
      toast.success('Chat deleted');
    } catch { toast.error('Delete failed'); }
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || busy) return;
    const newMsgs = [...messages, { role: 'user', content: text }];
    setMessages(newMsgs);
    setInput('');
    setBusy(true);
    try {
      const { data } = await api.post('/agent-chat/chat', {
        message: text,
        session_id: sessionId,
        history: newMsgs.slice(0, -1).map(({ role, content }) => ({ role, content })),
      });
      setMessages(m => [...m, {
        role: 'assistant', content: data.reply,
        tool_used: data.tool_used, tool_result: data.tool_result,
        latency_ms: data.latency_ms, actions: data.actions || [],
      }]);
      loadSessions();  // refresh sidebar
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Agent error');
      setMessages(m => [...m, { role: 'assistant', content: `Sorry — ${e?.message || 'something went wrong'}.` }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="h-[calc(100vh-56px)] flex bg-stone-50" data-testid="agent-chat-page">
      {/* -------- Sidebar: chat history -------- */}
      <aside className="w-64 border-r border-stone-200 bg-white flex flex-col shrink-0">
        <div className="p-3 border-b border-stone-200">
          <Button onClick={newChat} className="w-full h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-xs" data-testid="agent-new-chat">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Chat
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-2">
          <div className="text-[10px] uppercase tracking-wider text-stone-400 px-2 pb-1">Recent chats</div>
          {sessionsLoading && (
            <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-stone-400" /></div>
          )}
          {!sessionsLoading && sessions.length === 0 && (
            <div className="text-[11px] text-stone-400 px-2 py-3 text-center">No chats yet — ask me anything.</div>
          )}
          {sessions.map(s => (
            <button
              key={s.session_id}
              onClick={() => openSession(s.session_id)}
              data-testid={`agent-session-${s.session_id.slice(-6)}`}
              className={`group w-full text-left px-2 py-1.5 rounded-md text-xs mb-0.5 flex items-start gap-1.5 ${
                s.session_id === sessionId ? 'bg-indigo-50 text-indigo-900' : 'hover:bg-stone-100 text-stone-700'
              }`}
            >
              <MessageSquare className="w-3 h-3 mt-0.5 shrink-0 opacity-60" />
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium leading-tight">{s.title || '(new chat)'}</div>
                <div className="text-[10px] text-stone-400 mt-0.5">{formatTime(s.last_at)} · {s.turns} turn{s.turns === 1 ? '' : 's'}</div>
              </div>
              <span
                onClick={(e) => deleteSession(s.session_id, e)}
                className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-600 shrink-0"
                title="Delete chat"
              >
                <Trash2 className="w-3 h-3" />
              </span>
            </button>
          ))}
        </div>
      </aside>

      {/* -------- Chat column -------- */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="bg-white border-b border-stone-200 px-6 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-bold text-stone-900 text-sm">{user?.role === 'audiologist' ? 'My Agent' : 'HearClear AI Assistant'}</div>
            <div className="text-[11px] text-stone-500">
              {scope ? <>Scope: <b>{scope.scope_summary}</b> · <b>{scope.reachable_tickets.toLocaleString()}</b> patients reachable</> : 'Loading your scope…'}
            </div>
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-stone-600 cursor-pointer" title="When off, WhatsApps and calls wait for you to tap Send/Call">
            <Switch checked={autoSend} onCheckedChange={toggleAutoSend} data-testid="agent-auto-send-toggle" />
            Send straight away
          </label>
          <Badge variant="outline" className="text-[10px] font-mono">{user?.role}</Badge>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="max-w-3xl mx-auto space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-12">
                <div className="inline-flex w-14 h-14 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 items-center justify-center mb-3">
                  <Bot className="w-7 h-7 text-indigo-600" />
                </div>
                <div className="font-heading text-lg font-bold text-stone-800">Hi {user?.full_name?.split(' ')[0] || 'there'} 👋</div>
                <div className="text-sm text-stone-500 mt-1 max-w-md mx-auto">
                  Your HearClear agent. List your patients, follow up on WhatsApp, send product videos, create PDFs and place AI phone calls.
                </div>
                <div className="mt-6 flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
                  {QUICK_PROMPTS.map((q) => (
                    <button key={q} onClick={() => send(q)}
                      className="text-xs px-3 py-1.5 rounded-full border border-stone-300 bg-white hover:bg-stone-100 text-stone-700"
                      data-testid={`agent-quick-${q.slice(0, 12).replace(/\s+/g, '-')}`}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2.5 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0 mt-0.5">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </div>
                )}
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === 'user' ? 'bg-stone-900 text-white rounded-br-sm' : 'bg-white border border-stone-200 text-stone-800 rounded-bl-sm shadow-sm'}`}>
                  {m.role === 'assistant' ? <AgentMarkdown text={m.content} /> : <div className="whitespace-pre-wrap">{m.content}</div>}
                  {(m.actions || []).map((a) => <DraftActionCard key={a.action_id} action={a} onChange={(upd) => updateAction(i, upd)} />)}
                  {m.tool_used && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-indigo-600">
                      <Zap className="w-2.5 h-2.5" /> {m.tool_used}{m.latency_ms ? ` · ${m.latency_ms}ms` : ''}
                    </div>
                  )}
                </div>
                {m.role === 'user' && (
                  <div className="w-7 h-7 rounded-full bg-stone-200 flex items-center justify-center shrink-0 mt-0.5">
                    <UserIcon className="w-3.5 h-3.5 text-stone-600" />
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-white border border-stone-200 rounded-2xl rounded-bl-sm px-4 py-2.5 shadow-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        <div className="bg-white border-t border-stone-200 px-6 py-3">
          <div className="max-w-3xl mx-auto flex gap-2 items-end">
            <Textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask about your patients, trigger a WhatsApp, place a call, schedule a follow-up…  (⏎ to send)"
              className="min-h-[42px] max-h-32 text-sm resize-none"
              data-testid="agent-chat-input"
              disabled={busy}
            />
            <Button
              onClick={() => send()}
              disabled={busy || !input.trim()}
              className="h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
              data-testid="agent-chat-send"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <div className="max-w-3xl mx-auto mt-1.5 text-[10px] text-stone-400 text-center">
            Powered by Claude Sonnet · every message, PDF and call is logged on the patient's ticket
          </div>
        </div>
      </div>
    </div>
  );
}
